import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { importLibrary } from '@googlemaps/js-api-loader';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import Rotate90DegreesCcwRoundedIcon from '@mui/icons-material/Rotate90DegreesCcwRounded';
import Rotate90DegreesCwRoundedIcon from '@mui/icons-material/Rotate90DegreesCwRounded';
import ThreeSixtyRoundedIcon from '@mui/icons-material/ThreeSixtyRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import {
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { configureGoogleMapsLoader, googleMapsMapId } from '../../lib/googleMapsRuntime';
import {
  SITE_COVERAGE_RADIUS_M,
  type LatLngPoint,
  type SiteViewpoint,
} from '../../lib/employerSiteViewpoints';
import {
  SITE_ROLE_COLOR,
  buildingPinElement,
  siteRadiusRingOptions,
  viewpointPinElement,
  type SiteMarkerHandle,
} from './employerSiteMarkers';

type MapType = 'hybrid' | 'satellite' | 'roadmap' | 'terrain';

type Props = {
  building: LatLngPoint;
  employerName: string;
  viewpoints: SiteViewpoint[];
  activePanoId: string | null;
  onSelectViewpoint: (panoId: string) => void;
};

const SITE_RADIUS_M = SITE_COVERAGE_RADIUS_M;

function viewpointsSignature(viewpoints: SiteViewpoint[]): string {
  return viewpoints
    .map((v) => `${v.panoId}:${v.sequence}:${v.role}:${v.location.lat.toFixed(5)},${v.location.lng.toFixed(5)}`)
    .join('|');
}

export function EmployerSiteAerialStage({
  building,
  employerName,
  viewpoints,
  activePanoId,
  onSelectViewpoint,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const buildingMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const markersRef = useRef<Map<string, { marker: google.maps.marker.AdvancedMarkerElement; handle: SiteMarkerHandle }>>(
    new Map(),
  );
  const circleRef = useRef<google.maps.Circle | null>(null);
  const selectRef = useRef(onSelectViewpoint);
  selectRef.current = onSelectViewpoint;
  const AdvancedMarkerRef = useRef<typeof google.maps.marker.AdvancedMarkerElement | null>(null);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapType, setMapType] = useState<MapType>('hybrid');
  const [tilted, setTilted] = useState(false);
  const [heading, setHeading] = useState(0);
  const [showRadius, setShowRadius] = useState(true);
  const [streetViewOpen, setStreetViewOpen] = useState(false);

  const { lat, lng } = building;
  const vpKey = useMemo(() => viewpointsSignature(viewpoints), [viewpoints]);

  // Create the map once; move center on building changes.
  useEffect(() => {
    if (!configureGoogleMapsLoader()) {
      setError('Google Maps API key is not configured.');
      return;
    }

    let cancelled = false;
    setError(null);

    void (async () => {
      try {
        const { ControlPosition } = await importLibrary('core');
        const { Map } = await importLibrary('maps');
        if (cancelled || !hostRef.current) return;

        if (!mapRef.current) {
          mapRef.current = new Map(hostRef.current, {
            center: { lat, lng },
            zoom: 18,
            mapId: googleMapsMapId(),
            mapTypeId: 'hybrid',
            mapTypeControl: false,
            streetViewControl: true,
            streetViewControlOptions: { position: ControlPosition.RIGHT_BOTTOM },
            fullscreenControl: true,
            fullscreenControlOptions: { position: ControlPosition.RIGHT_BOTTOM },
            rotateControl: false,
            gestureHandling: 'greedy',
            tilt: 0,
            heading: 0,
          });
          const pano = mapRef.current.getStreetView();
          pano.addListener('visible_changed', () => {
            setStreetViewOpen(pano.getVisible());
          });
        } else {
          mapRef.current.setCenter({ lat, lng });
        }
        if (!cancelled) setReady(true);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the map.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  // Dispose map host only on unmount.
  useEffect(() => {
    return () => {
      for (const entry of markersRef.current.values()) {
        entry.marker.map = null;
      }
      markersRef.current.clear();
      buildingMarkerRef.current && (buildingMarkerRef.current.map = null);
      buildingMarkerRef.current = null;
      circleRef.current?.setMap(null);
      circleRef.current = null;
      if (hostRef.current) hostRef.current.replaceChildren();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;

    let cancelled = false;

    void (async () => {
      try {
        if (!AdvancedMarkerRef.current) {
          const { AdvancedMarkerElement } = await importLibrary('marker');
          AdvancedMarkerRef.current = AdvancedMarkerElement;
        }
        const AdvancedMarkerElement = AdvancedMarkerRef.current;
        if (cancelled || !AdvancedMarkerElement) return;

        if (buildingMarkerRef.current) {
          buildingMarkerRef.current.position = { lat, lng };
          buildingMarkerRef.current.title = employerName;
          buildingMarkerRef.current.content = buildingPinElement(employerName);
        } else {
          buildingMarkerRef.current = new AdvancedMarkerElement({
            map,
            position: { lat, lng },
            title: employerName,
            zIndex: 40,
            content: buildingPinElement(employerName),
          });
        }

        const nextIds = new Set(viewpoints.map((v) => v.panoId));
        for (const [panoId, entry] of markersRef.current) {
          if (!nextIds.has(panoId)) {
            entry.marker.map = null;
            markersRef.current.delete(panoId);
          }
        }

        for (const viewpoint of viewpoints) {
          const existing = markersRef.current.get(viewpoint.panoId);
          if (existing) {
            existing.marker.position = viewpoint.location;
            existing.marker.title = `${viewpoint.title} — ${viewpoint.purpose}`;
            existing.marker.zIndex = 20 + viewpoint.sequence;
            existing.handle.setActive(viewpoint.panoId === activePanoId);
            continue;
          }

          const handle = viewpointPinElement(viewpoint, viewpoint.panoId === activePanoId);
          const marker = new AdvancedMarkerElement({
            map,
            position: viewpoint.location,
            title: `${viewpoint.title} — ${viewpoint.purpose}`,
            zIndex: 20 + viewpoint.sequence,
            gmpClickable: true,
            content: handle.element,
          });
          marker.addListener('click', () => selectRef.current(viewpoint.panoId));
          markersRef.current.set(viewpoint.panoId, { marker, handle });
        }
      } catch {
        // Markers are additive; the aerial imagery is still usable without them.
      }
    })();

    return () => {
      cancelled = true;
    };
    // activePanoId handled in a separate effect so markers are not rebuilt on selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, lat, lng, employerName, vpKey]);

  useEffect(() => {
    for (const [panoId, entry] of markersRef.current) {
      entry.handle.setActive(panoId === activePanoId);
    }
  }, [activePanoId, vpKey, ready]);

  useEffect(() => {
    mapRef.current?.setMapTypeId(mapType);
  }, [mapType, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    map.setTilt(tilted ? 45 : 0);
    map.setHeading(tilted ? heading : 0);
  }, [ready, tilted, heading]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;

    let cancelled = false;
    void (async () => {
      try {
        const { Circle } = await importLibrary('maps');
        if (cancelled) return;
        if (!showRadius) {
          circleRef.current?.setMap(null);
          circleRef.current = null;
          return;
        }
        if (circleRef.current) {
          circleRef.current.setCenter({ lat, lng });
          circleRef.current.setRadius(SITE_RADIUS_M);
          circleRef.current.setMap(map);
          return;
        }
        circleRef.current = new Circle({
          map,
          center: { lat, lng },
          radius: SITE_RADIUS_M,
          ...siteRadiusRingOptions(),
        });
      } catch {
        // Ring is decoration only.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, showRadius, lat, lng]);

  const recenter = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setCenter({ lat, lng });
    map.setZoom(18);
  }, [lat, lng]);

  if (error) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', p: 3 }}>
        <Typography variant="body2" color="error.main" textAlign="center">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <Box ref={hostRef} sx={{ height: '100%', width: '100%' }} />

      {!ready ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {!streetViewOpen ? (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          right: 10,
          flexWrap: 'wrap',
          gap: 0.75,
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mapType}
          onChange={(_, next: MapType | null) => next && setMapType(next)}
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.25, py: 0.4 },
          }}
        >
          <ToggleButton value="hybrid">Satellite + labels</ToggleButton>
          <ToggleButton value="satellite">Satellite</ToggleButton>
          <ToggleButton value="roadmap">Map</ToggleButton>
          <ToggleButton value="terrain">Terrain</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '& .MuiToggleButton-root': { px: 0.9, py: 0.4 },
          }}
        >
          <ToggleButton
            value="tilt"
            selected={tilted}
            onClick={() => setTilted((v) => !v)}
            title="45° building view"
          >
            <ThreeSixtyRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="ccw"
            disabled={!tilted}
            onClick={() => setHeading((h) => (h + 270) % 360)}
            title="Rotate left"
          >
            <Rotate90DegreesCcwRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="cw"
            disabled={!tilted}
            onClick={() => setHeading((h) => (h + 90) % 360)}
            title="Rotate right"
          >
            <Rotate90DegreesCwRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="radius"
            selected={showRadius}
            onClick={() => setShowRadius((v) => !v)}
            title={`${SITE_RADIUS_M} m site ring`}
          >
            <RadioButtonUncheckedRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="recenter" onClick={recenter} title="Recentre on the building">
            <CenterFocusStrongRoundedIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      ) : null}

      {!streetViewOpen ? (
      <Stack
        direction="row"
        spacing={1.25}
        sx={{
          position: 'absolute',
          bottom: 10,
          left: 10,
          px: 1.25,
          py: 0.6,
          borderRadius: 999,
          bgcolor: 'rgba(17,24,39,0.78)',
          color: '#fff',
          flexWrap: 'wrap',
          rowGap: 0.25,
        }}
      >
        <LegendDot color={SITE_ROLE_COLOR.BUILDING} label="Employer address" />
        <LegendDot color={SITE_ROLE_COLOR.ENTRANCE} label="Nearest camera" />
        <LegendDot color={SITE_ROLE_COLOR.EXIT} label="Opposite side" />
        <LegendDot color={SITE_ROLE_COLOR.SIDE} label="Side approach" />
      </Stack>
      ) : null}

      {!streetViewOpen && viewpoints.length > 0 ? (
        <Tooltip title="Numbered pins open Street View from that side">
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 56,
              right: 10,
              px: 1,
              py: 0.4,
              borderRadius: 1,
              bgcolor: 'rgba(17,24,39,0.78)',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            {viewpoints.length} street cameras around this site
          </Typography>
        </Tooltip>
      ) : null}
    </Box>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
    </Stack>
  );
}
